import { useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Unternehmen, Zahlstatus } from "@kundeninfo/api-client";
import { tagPlus } from "@kundeninfo/ui";
import { Eingabe, Hinweis, Knopf, Pillenreihe } from "@kundeninfo/ui/me-design";
import { UnternehmensChips, sichtFuer } from "../../components/Filterkopf.js";
import { sharedAssignmentLists } from "../../components/KontierungFelder.js";
import { useStabileListe } from "../../components/stabileListe.js";
import { formatBetrag, formatDatum } from "../../format.js";
import type { TicketWeg } from "../suppliers/LiefervertragObjekt.js";
import { today, toggleState, useAreaQuery } from "../suppliers/shared.js";
import { EingangsrechnungObjekt, useIncomingActions } from "./EingangsrechnungObjekt.js";
import type { IncomingListClient } from "./KundeEingangsrechnungen.js";
import { INCOMING_TEXT_WEB as T } from "./texts.js";
import "./IncomingInvoices.css";

/**
 * Der BEREICH „Eingangsrechnungen“ (Spec „Bereiche“; seit „Öffnen statt Springen“ Aufgabe 3 als Liste von
 * `EingangsrechnungObjekt`, K13) — der Arbeitsplatz für „was ist diese Woche zu zahlen“: Unternehmens-Chips (R21),
 * Zahlstatus-Chips (in der Adresse, A-W3) und „fällig bis“ (Vorgabe heute + 7 Tage); darüber „Zu zahlen bis ‹Datum›: n
 * Rechnungen, Summe“ (rechnet der Server), darunter die Objekte in seiner Ordnung (das Schlechteste oben). Derselbe Bau
 * wie in der Akte, dazu der Lieferant im Bezug und der Sprungknopf in die Akte (`sprungZiel`, Quelle `search`). Bearbeiten,
 * Zahlung, Storno und Ticket an Ort und Stelle (`useIncomingActions`); danach lädt die Liste im Stehen nach. Die Leiste
 * wechselt ihre Gestalt durch keine Wahl; ein Filterwechsel hält sie an ihrer Stelle (`useAreaQuery`).
 */
type PaymentState = Zahlstatus["state"];
const STATES: readonly PaymentState[] =
  ["OVERDUE", "DUE_SOON", "PART_PAID", "CREDIT_OPEN", "OPEN", "ASSUMED_PAID", "PAID", "CANCELLED"];
const DUE_DAYS = 7;

export function BereichEingangsrechnungen(p: {
  client: IncomingListClient & Pick<ApiClient, "listIncomingArea">; unternehmen: Unternehmen[];
  aktivesUnternehmen: string | null;
  /** Die Zahlstatus-Chips aus der Adresse (leer = alle) und der Weg, sie zu ändern (A-W3). */
  states: readonly PaymentState[]; onStates: (states: PaymentState[]) => void;
  /** Der Ticketweg: ohne ihn kein „Ticket anlegen“ im „⋯“. */
  ticket?: TicketWeg;
}): ReactElement {
  const [extra, setExtra] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [lists] = useState(() => sharedAssignmentLists(p.client));
  const liste = useRef<HTMLDivElement | null>(null);
  const [dueUntil, setDueUntil] = useState(() => tagPlus(today(), DUE_DAYS));
  const companyIds = useStabileListe(sichtFuer(p.aktivesUnternehmen, extra));
  const states = p.states;
  const query = {
    ...(companyIds === undefined ? {} : { companyIds }), ...(dueUntil === "" ? {} : { dueUntil }),
    ...(states.length === 0 ? {} : { states: [...states] }),
  };
  const { data, error, toolbar, change, reload } = useAreaQuery(query, (q) => p.client.listIncomingArea(q), T.loadFailed);
  const toggle = (s: PaymentState): void => change(() => p.onStates(toggleState(STATES, states, s)));
  const nachladen = (id: string): void =>
    reload([...(liste.current?.children ?? [])].find((e) => e.getAttribute("data-incoming-id") === id) ?? null);
  const aktionen = useIncomingActions({ client: p.client, lists, companies: p.unternehmen,
    ...(p.ticket === undefined ? {} : { ticket: p.ticket }), onDone: nachladen });

  const line = data?.payableUntil ?? null;
  const sums = line === null ? "" : line.sums.length === 0 ? formatBetrag(0, "EUR")
    : line.sums.map((s) => formatBetrag(s.amount, s.currency)).join(" + ");

  return (
    <div className="inc inc--area">
      <div ref={toolbar} className="inc__toolbar">
        <UnternehmensChips label={T.companiesLabel} companies={p.unternehmen} aktivId={p.aktivesUnternehmen} zusatz={extra}
          onZusatz={(ids) => change(() => setExtra(ids))} />
        <div className="inc__chips" role="group" aria-label={T.statesLabel}>
          <Pillenreihe>
            <Knopf art="sekundaer" gestalt="pille" gedrueckt={states.length === 0} onClick={() => change(() => p.onStates([]))}>
              {T.allStates}
            </Knopf>
            {STATES.map((s) => (
              <Knopf key={s} art="sekundaer" gestalt="pille" gedrueckt={states.includes(s)} onClick={() => toggle(s)}>
                {T.stateChips[s]}
              </Knopf>
            ))}
          </Pillenreihe>
        </div>
        <Eingabe art="datum" className="inc__due" beschriftung={T.dueUntil} wert={dueUntil}
          onGeaendert={(e) => change(() => setDueUntil(e.wert))} />
      </div>

      {error !== null && <Hinweis art="fehler">{error}</Hinweis>}
      {aktionen.fehler !== null && <Hinweis art="fehler">{aktionen.fehler}</Hinweis>}
      {line !== null && (
        <p className="inc__payable-until" role="status">{T.payableUntil(formatDatum(line.until), line.count, sums)}</p>
      )}
      {data === null && error === null && <p className="inc__muted" role="status">{T.loading}</p>}
      {data !== null && data.items.length === 0 && <p className="inc__muted">{T.empty}</p>}
      {data !== null && data.items.length > 0 && (
        // W-4: alle Objekte direkte Geschwister EINES Behälters.
        <div ref={liste} className="inc__objekte">
          {data.items.map((inv) => (
            <EingangsrechnungObjekt key={inv.id} rechnung={inv} ort="einkauf" aufgeklappt={open === inv.id}
              onAufgeklappt={(offen) => setOpen(offen ? inv.id : null)} client={p.client} lists={lists}
              companies={p.unternehmen} mehrereUnternehmen={companyIds !== undefined} mitTicket={aktionen.mitTicket}
              onAktion={(a) => aktionen.start(a, inv)} />
          ))}
        </div>
      )}
      {aktionen.panels}
    </div>
  );
}
